import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { API_BASE_URL } from 'config/api'
import { ProductImage, getImageUrl, getImageAlt } from 'utils/imageUtils'

export const Lightbox = ({
  images,
  startIndex,
  onClose,
  productTitle,
  autoPlay = false,
  autoPlayInterval = 4000,
}: {
  images: ProductImage[]
  startIndex: number
  onClose: () => void
  productTitle?: string
  autoPlay?: boolean
  autoPlayInterval?: number
}) => {
  const [currentIndex, setCurrentIndex] = useState(startIndex)
  const [isAutoPlaying, setIsAutoPlaying] = useState(false)
  const autoPlayTimerRef = useRef<NodeJS.Timeout | null>(null)

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
    if (isAutoPlaying) pauseAutoPlay()
  }, [images.length, isAutoPlaying])

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
    if (isAutoPlaying) pauseAutoPlay()
  }, [images.length, isAutoPlaying])

  // --- AutoPlay Logic for Lightbox ---
  const startAutoPlay = useCallback(() => {
    if (!autoPlay || images.length <= 1) return

    setIsAutoPlaying(true)
    autoPlayTimerRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
    }, autoPlayInterval)
  }, [autoPlay, autoPlayInterval, images.length])

  const pauseAutoPlay = useCallback(() => {
    setIsAutoPlaying(false)
    if (autoPlayTimerRef.current) {
      clearInterval(autoPlayTimerRef.current)
      autoPlayTimerRef.current = null
    }
  }, [])

  const toggleAutoPlay = () => {
    if (isAutoPlaying) {
      pauseAutoPlay()
    } else {
      startAutoPlay()
    }
  }

  // Start autoplay on mount
  useEffect(() => {
    if (autoPlay) {
      const timer = setTimeout(() => startAutoPlay(), 1000) // Delay start
      return () => clearTimeout(timer)
    }
  }, [autoPlay, startAutoPlay])

  // Cleanup autoplay
  useEffect(() => {
    return () => {
      if (autoPlayTimerRef.current) {
        clearInterval(autoPlayTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') goToPrevious()
      if (e.key === 'ArrowRight') goToNext()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose, goToPrevious, goToNext])

  return (
    <div
      className='fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4'
      onClick={onClose}
      role='dialog'
      aria-modal='true'
      onMouseEnter={pauseAutoPlay}
      onMouseLeave={() => autoPlay && startAutoPlay()}
    >
      <div
        className='relative w-full h-full max-w-4xl max-h-full flex items-center'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='w-full h-full flex items-center justify-center overflow-hidden'>
          {images.map((image, index) => (
            <img
              src={getImageUrl(image.image)}
              key={index}
              alt={getImageAlt(image.alt, productTitle, index)}
              title={image.title || getImageAlt(image.alt, productTitle, index)}
              className={`absolute max-w-full max-h-full object-contain transition-all duration-500 ${
                index === currentIndex
                  ? 'opacity-100 scale-100'
                  : 'opacity-0 scale-95'
              }`}
            />
          ))}
        </div>

        {/* AutoPlay Toggle & Counter */}
        <div className='absolute top-4 left-4 flex items-center space-x-2 space-x-reverse'>
          <div className='bg-black/60 text-white px-3 py-1 rounded-md text-sm font-medium'>
            {images.length} / {currentIndex + 1}
          </div>
          {autoPlay && images.length > 1 && (
            <button
              onClick={toggleAutoPlay}
              className='bg-black/60 text-white p-2 rounded-md hover:bg-black/80 transition-colors'
              aria-label={isAutoPlaying ? 'توقف پخش خودکار' : 'شروع پخش خودکار'}
            >
              {isAutoPlaying ? (
                <div className='w-3 h-3 bg-white rounded-sm'></div>
              ) : (
                <div className='w-0 h-0 border-l-[8px] border-l-white border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent ml-0.5'></div>
              )}
            </button>
          )}
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className='absolute top-4 right-4 z-50 p-2 bg-white/80 text-gray-900 rounded-full hover:bg-white transition-all'
          aria-label='بستن'
        >
          <X size={24} />
        </button>

        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className='absolute left-0 md:-left-12 top-1/2 -translate-y-1/2 p-3 bg-white/80 text-gray-900 rounded-full hover:bg-white transition-all'
              aria-label='قبلی'
            >
              <ChevronLeft size={28} />
            </button>
            <button
              onClick={goToNext}
              className='absolute right-0 md:-right-12 top-1/2 -translate-y-1/2 p-3 bg-white/80 text-gray-900 rounded-full hover:bg-white transition-all'
              aria-label='بعدی'
            >
              <ChevronRight size={28} />
            </button>
          </>
        )}
      </div>
    </div>
  )
}
